import React, { useEffect, useState } from 'react';
import axios from 'axios';

const CoachingFeesSummary = () => {
  const [students, setStudents] = useState([]);
  const [payments, setPayments] = useState([]);
  const [selectedClass, setSelectedClass] = useState('All');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    const studentsRes = await axios.get('get_coaching_students_list.php', { withCredentials: true });
    const paymentsRes = await axios.get('get_coaching_fees.php', { withCredentials: true });
    setStudents(studentsRes.data);
    setPayments(paymentsRes.data);
  };

  // Compute unique class names for dropdown
  const classList = [...new Set(students.map(stu => stu.class_name))].sort();

  const calculatePaid = (studentId, type) => {
    const studentPayments = payments.filter(p => p.student_id === studentId);
    if (type === 'tuition') {
      return studentPayments.reduce((acc, curr) => acc + (Number(curr.amount) || 0), 0);
    } else if (type === 'van') {
      return studentPayments.reduce((acc, curr) => acc + (Number(curr.van_fee_amount) || 0), 0);
    }
    return 0;
  };

  // Filtered by class if selected
  const filteredStudents = selectedClass === 'All' ?
    students :
    students.filter(stu => stu.class_name === selectedClass);

  // Totals for filtered students
  let totals = {
    tuitionFee: 0,
    vanFee: 0,
    tuitionPaid: 0,
    vanPaid: 0,
    tuitionDue: 0,
    vanDue: 0,
  };

  // Build table rows for printing
  const tableRows = filteredStudents.map((student, index) => {
    const tuitionFee = Number(student.tuition_fee) || 0;
    const vanFee = Number(student.van_fee) || 0;
    const tuitionPaid = calculatePaid(student.id, 'tuition');
    const vanPaid = calculatePaid(student.id, 'van');
    const tuitionDue = tuitionFee - tuitionPaid;
    const vanDue = vanFee - vanPaid;

    // sum totals for printing
    totals.tuitionFee += tuitionFee;
    totals.vanFee += vanFee;
    totals.tuitionPaid += tuitionPaid;
    totals.vanPaid += vanPaid;
    totals.tuitionDue += tuitionDue;
    totals.vanDue += vanDue;

    return `
      <tr>
        <td>${index + 1}</td>
        <td>${student.full_name}</td>
        <td>${student.class_name}</td>
        <td>${tuitionFee}</td>
        <td>${vanFee}</td>
        <td>${tuitionPaid}</td>
        <td>${vanPaid}</td>
        <td>${tuitionDue}</td>
        <td>${vanDue}</td>
      </tr>
    `;
  }).join('');

  const totalRow = `
    <tr style="font-weight:bold; background:#ffe0b2;">
      <td colspan="3">Total</td>
      <td>${totals.tuitionFee}</td>
      <td>${totals.vanFee}</td>
      <td>${totals.tuitionPaid}</td>
      <td>${totals.vanPaid}</td>
      <td>${totals.tuitionDue}</td>
      <td>${totals.vanDue}</td>
    </tr>
  `;

  const handlePrint = () => {
    // Reset totals before print calculation
    totals = {
      tuitionFee: 0,
      vanFee: 0,
      tuitionPaid: 0,
      vanPaid: 0,
      tuitionDue: 0,
      vanDue: 0,
    };

    // Recalculate rows and totals for print (to keep accurate with current filtered data)
    const printRows = filteredStudents.map((student, index) => {
      const tuitionFee = Number(student.tuition_fee) || 0;
      const vanFee = Number(student.van_fee) || 0;
      const tuitionPaid = calculatePaid(student.id, 'tuition');
      const vanPaid = calculatePaid(student.id, 'van');
      const tuitionDue = tuitionFee - tuitionPaid;
      const vanDue = vanFee - vanPaid;

      totals.tuitionFee += tuitionFee;
      totals.vanFee += vanFee;
      totals.tuitionPaid += tuitionPaid;
      totals.vanPaid += vanPaid;
      totals.tuitionDue += tuitionDue;
      totals.vanDue += vanDue;

      return `
        <tr>
          <td>${index + 1}</td>
          <td>${student.full_name}</td>
          <td>${student.class_name}</td>
          <td>${tuitionFee}</td>
          <td>${vanFee}</td>
          <td>${tuitionPaid}</td>
          <td>${vanPaid}</td>
          <td>${tuitionDue}</td>
          <td>${vanDue}</td>
        </tr>
      `;
    }).join('');

    const printTotal = `
      <tr style="font-weight:bold; background:#ffe0b2;">
        <td colspan="3">Total</td>
        <td>${totals.tuitionFee}</td>
        <td>${totals.vanFee}</td>
        <td>${totals.tuitionPaid}</td>
        <td>${totals.vanPaid}</td>
        <td>${totals.tuitionDue}</td>
        <td>${totals.vanDue}</td>
      </tr>
    `;

    const printWindow = window.open('', '', 'width=1200,height=900');
    printWindow.document.write(`
      <html>
        <head>
          <title>Coaching Students Fee Summary</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 0; background: #fff; }
            .print-box {
              border: 2px solid #222;
              padding: 32px 36px;
              margin: 28px auto;
              width: 96%;
              background: #fff;
            }
            .school-logo {
              display: block;
              margin: 0 auto 10px auto;
              width: 75px;
              height: 75px;
              object-fit: contain;
            }
            .school-title {
              text-align: center;
              font-weight: bold;
              font-size: 25px;
              margin-bottom: 2px;
            }
            .school-address {
              text-align: center;
              font-size: 15px;
              color: #555;
              margin-bottom: 10px;
            }
            .summary-heading {
              text-align: center;
              font-size: 18px;
              font-weight: 600;
              margin: 16px 0;
            }
            table {
              border-collapse: collapse;
              width: 100%;
              margin-bottom: 16px;
            }
            th, td {
              border: 1px solid #333;
              padding: 9px 6px;
              text-align: center;
              font-size: 16px;
            }
            th {
              background: #f5f5f5;
            }
            tr:last-child td {
              font-weight: bold;
            }
          </style>
        </head>
        <body>
          <div class="print-box">
            <img src="/school-logo.jpg" alt="School Logo" class="school-logo"/>
            <div class="school-title">OXFORD CONVENT ENGLISH MEDIUM SCHOOL, KHAJURI</div>
            <div class="school-address">Khajuri - 585 314, Tq. Aland, Dist. Kalaburagi</div>
            <div class="summary-heading">Coaching Students Fee Summary</div>
            <table>
              <thead>
                <tr>
                  <th>Sl No.</th>
                  <th>Name</th>
                  <th>Class</th>
                  <th>Tuition Fee</th>
                  <th>Van Fee</th>
                  <th>Tuition Paid</th>
                  <th>Van Paid</th>
                  <th>Tuition Due</th>
                  <th>Van Due</th>
                </tr>
              </thead>
              <tbody>
                ${printRows}
                ${printTotal}
              </tbody>
            </table>
          </div>
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
    printWindow.close();
  };

  // Reset totals before rendering on web
  totals = {
    tuitionFee: 0,
    vanFee: 0,
    tuitionPaid: 0,
    vanPaid: 0,
    tuitionDue: 0,
    vanDue: 0,
  };

  const cellStyle = { border: '1px solid #333', padding: '9px 6px', textAlign: 'center' };
  const thStyle = { ...cellStyle, background: '#f5f5f5' };

  return (
    <div>
      <div style={{ border: '2px solid #222', margin: 28, padding: 28, borderRadius: 8, background: '#fff' }}>
        {/* Header with logo and school info */}
        <img
          src="/school-logo.jpg"
          alt="School Logo"
          style={{ display: 'block', margin: '0 auto 10px auto', width: 65, height: 65, objectFit: 'contain' }}
        />
        <div style={{ textAlign: 'center', fontWeight: 'bold', fontSize: 22 }}>OXFORD CONVENT ENGLISH MEDIUM SCHOOL, KHAJURI</div>
        <div style={{ textAlign: 'center', fontSize: 14, color: '#555', marginBottom: 10 }}>Khajuri - 585 314, Tq. Aland, Dist. Kalaburagi</div>
        <div style={{ textAlign: 'center', fontSize: 17, fontWeight: 600, margin: '15px 0' }}>Coaching Students Fee Summary</div>

        {/* Class Dropdown Filter + Print Button */}
        <div style={{ marginBottom: 16, textAlign: 'center' }}>
          <label htmlFor="class-filter" style={{ fontWeight: 500, fontSize: 16, marginRight: 10 }}>
            Sort by Class:&nbsp;
          </label>
          <select
            id="class-filter"
            value={selectedClass}
            onChange={e => setSelectedClass(e.target.value)}
            style={{ fontSize: 16, padding: '5px 15px', minWidth: 120, borderRadius: 5 }}
          >
            <option value="All">All</option>
            {classList.map((className, idx) => (
              <option key={idx} value={className}>
                {className}
              </option>
            ))}
          </select>
          <button
            onClick={handlePrint}
            style={{ marginLeft: 18, padding: "7px 15px", fontWeight: 600, fontSize: 15, borderRadius: 4, background: "#0a53be", color: "#fff", border: "none" }}
          >
            🖨️ Print Table
          </button>
        </div>

        {/* Table with borders */}
        <table style={{ borderCollapse: "collapse", width: "100%", background: "#fff" }}>
          <thead>
            <tr>
              <th style={thStyle}>Sl No.</th>
              <th style={thStyle}>Name</th>
              <th style={thStyle}>Class</th>
              <th style={thStyle}>Tuition Fee</th>
              <th style={thStyle}>Van Fee</th>
              <th style={thStyle}>Tuition Paid</th>
              <th style={thStyle}>Van Paid</th>
              <th style={thStyle}>Tuition Due</th>
              <th style={thStyle}>Van Due</th>
            </tr>
          </thead>
          <tbody>
            {filteredStudents.map((student, index) => {
              const tuitionFee = Number(student.tuition_fee) || 0;
              const vanFee = Number(student.van_fee) || 0;
              const tuitionPaid = calculatePaid(student.id, 'tuition');
              const vanPaid = calculatePaid(student.id, 'van');
              const tuitionDue = tuitionFee - tuitionPaid;
              const vanDue = vanFee - vanPaid;

              totals.tuitionFee += tuitionFee;
              totals.vanFee += vanFee;
              totals.tuitionPaid += tuitionPaid;
              totals.vanPaid += vanPaid;
              totals.tuitionDue += tuitionDue;
              totals.vanDue += vanDue;

              return (
                <tr key={student.id}>
                  <td style={cellStyle}>{index + 1}</td>
                  <td style={cellStyle}>{student.full_name}</td>
                  <td style={cellStyle}>{student.class_name}</td>
                  <td style={cellStyle}>{tuitionFee}</td>
                  <td style={cellStyle}>{vanFee}</td>
                  <td style={cellStyle}>{tuitionPaid}</td>
                  <td style={cellStyle}>{vanPaid}</td>
                  <td style={cellStyle}>{tuitionDue}</td>
                  <td style={cellStyle}>{vanDue}</td>
                </tr>
              );
            })}
            <tr style={{ fontWeight: 'bold', background: '#ffe0b2' }}>
              <td style={cellStyle} colSpan="3">Total</td>
              <td style={cellStyle}>{totals.tuitionFee}</td>
              <td style={cellStyle}>{totals.vanFee}</td>
              <td style={cellStyle}>{totals.tuitionPaid}</td>
              <td style={cellStyle}>{totals.vanPaid}</td>
              <td style={cellStyle}>{totals.tuitionDue}</td>
              <td style={cellStyle}>{totals.vanDue}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default CoachingFeesSummary;
